import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import i18n from 'i18n-js';
import {
    Box,
    IconButton,
    Paper,
    Switch,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Toolbar,
    Tooltip,
    Typography,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';

import agent from 'api/agent';
import { AppVersion, DistributionApp } from 'api/models';
import globalStore from 'store/globalStore';
import { formatDateTimeGermany } from 'helpers/datetimeHelper';
import GitHubTokenForm from './components/GitHubTokenForm';
import { SkeletonBlock, TableSkeleton } from 'components/shared';
import { useStyles } from './styles';

export default function AppDistribution() {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [app, setApp] = useState<DistributionApp>('waiter');
    const [versions, setVersions] = useState<AppVersion[] | null>(null);
    const [pendingVersion, setPendingVersion] = useState('');

    useEffect(() => {
        dispatch(globalStore.actionCreators.showSpiner());
        agent.AppUpdates.GetVersions(app)
            .then(setVersions)
            .catch((e) => {
                setVersions([]);
                dispatch(globalStore.actionCreators.showToaster('error', e));
            })
            .finally(() => dispatch(globalStore.actionCreators.hideSpiner()));
    }, [app]);

    function toggleEnrollment(version: AppVersion) {
        const enrolled = !version.enrolled;
        setPendingVersion(version.fullVersion);
        agent.AppUpdates.SetEnrollment(app, version.fullVersion, enrolled)
            .then(() =>
                setVersions((current) =>
                    (current || []).map((item) =>
                        item.fullVersion === version.fullVersion ? { ...item, enrolled } : item
                    )
                )
            )
            .catch((e) => dispatch(globalStore.actionCreators.showToaster('error', e)))
            .finally(() => setPendingVersion(''));
    }

    function downloadVersion(fullVersion: string) {
        dispatch(globalStore.actionCreators.showSpiner());
        agent.AppUpdates.GetVersionDownloadLink(app, fullVersion)
            .then((link) => window.open(link.url, '_blank'))
            .catch((e) => dispatch(globalStore.actionCreators.showToaster('error', e)))
            .finally(() => dispatch(globalStore.actionCreators.hideSpiner()));
    }

    return (
        <Box className={classes.root}>
            <Toolbar>
                <Tabs
                    value={app}
                    onChange={(_event, value) => setApp(value)}
                    aria-label="app distribution tabs"
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab value="waiter" label={i18n.t('appDistribution.waiter')} />
                    <Tab value="register" label={i18n.t('appDistribution.register')} />
                </Tabs>
            </Toolbar>

            <Box px={2}>
                <GitHubTokenForm />
            </Box>

            <Box px={2}>
                <Paper style={{ position: 'relative' }}>
                    <Typography className={classes.enrollmentHint}>
                        {i18n.t('appDistribution.enrollmentHint')}
                    </Typography>
                    <TableContainer id="container">
                        <Table stickyHeader aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.tableCellHeader}>
                                        <Box className={classes.tableCellHeaderContainer}>
                                            {i18n.t('appDistribution.version')}
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.tableCellHeader}>
                                        <Box className={classes.tableCellHeaderContainer}>
                                            {i18n.t('appDistribution.publishedAt')}
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.tableCellHeader}>
                                        <Box className={classes.tableCellHeaderContainer}>
                                            {i18n.t('appDistribution.size')}
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.tableCellHeader}>
                                        <Box className={classes.tableCellHeaderContainer}>
                                            {i18n.t('appDistribution.commit')}
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.tableCellHeader}>
                                        <Box className={classes.tableCellHeaderContainer}>
                                            {i18n.t('appDistribution.sha256')}
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.tableCellHeader}>
                                        <Box className={classes.tableCellHeaderContainer}>
                                            {i18n.t('appDistribution.enrolled')}
                                        </Box>
                                    </TableCell>
                                    <TableCell className={classes.tableCellHeader}></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {versions && versions.length > 0
                                    ? versions.map((version) => (
                                          <TableRow key={version.fullVersion}>
                                              <TableCell>
                                                  <Box component="span">{version.fullVersion}</Box>
                                              </TableCell>
                                              <TableCell>
                                                  <Box component="span">
                                                      {formatDateTimeGermany(
                                                          new Date(version.publishedAt)
                                                      )}
                                                  </Box>
                                              </TableCell>
                                              <TableCell>
                                                  <Box component="span">
                                                      {(version.sizeBytes / 1024 / 1024).toFixed(1)}{' '}
                                                      MB
                                                  </Box>
                                              </TableCell>
                                              <TableCell>
                                                  <Tooltip title={version.commit || ''}>
                                                      <Box
                                                          component="span"
                                                          className={classes.monospace}
                                                      >
                                                          {version.commit?.slice(0, 7)}
                                                      </Box>
                                                  </Tooltip>
                                              </TableCell>
                                              <TableCell>
                                                  <Tooltip title={version.sha256 || ''}>
                                                      <Box
                                                          component="span"
                                                          className={classes.monospace}
                                                      >
                                                          {version.sha256?.slice(0, 12)}
                                                      </Box>
                                                  </Tooltip>
                                              </TableCell>
                                              <TableCell align="left">
                                                  <Switch
                                                      edge="start"
                                                      color="primary"
                                                      checked={version.enrolled}
                                                      disabled={
                                                          pendingVersion === version.fullVersion
                                                      }
                                                      onChange={() => toggleEnrollment(version)}
                                                  />
                                              </TableCell>
                                              <TableCell align="left">
                                                  <IconButton
                                                      aria-label="download"
                                                      size="small"
                                                      edge="start"
                                                      className={classes.iconButton}
                                                      onClick={() =>
                                                          downloadVersion(version.fullVersion)
                                                      }
                                                  >
                                                      <GetAppIcon
                                                          fontSize="medium"
                                                          color="primary"
                                                      />
                                                  </IconButton>
                                              </TableCell>
                                          </TableRow>
                                      ))
                                    : !!versions && (
                                          <TableRow>
                                              <TableCell
                                                  style={{ borderBottom: 'none', padding: 20 }}
                                                  colSpan={7}
                                                  align="center"
                                              >
                                                  {i18n.t('appDistribution.noVersions')}
                                              </TableCell>
                                          </TableRow>
                                      )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TableSkeleton
                        isLoading={versions === null}
                        columnWidths={[
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            undefined,
                            100,
                            40,
                        ]}
                        rowCount={8}
                    >
                        <SkeletonBlock height={20} width="60%" />
                    </TableSkeleton>
                </Paper>
            </Box>
        </Box>
    );
}
